import { ApiError } from '@/api/client'
import type { CreateRolloutAck, FamilyStart, FamilyVariante, RolloutFamily } from '@/api/priceRollout'
import { fehlerText, grundText, startAmZiel, startMeldung } from './rolloutView'

/**
 * Sammelstart Basis (Plan 2026-10-27-rollout-sammelstart, Entscheidungen 28.10.): mehrere Varianten
 * mit einer Bestätigung, aber strikt nacheinander über den bestehenden Einzelstart. Vor jedem Start
 * wird die Familie neu geladen (Review R1: der Server übernimmt das gesendete Ziel); gesendet wird
 * nur, wenn der frische Stand dem bestätigten entspricht.
 */
export const SAMMEL_MAX = 50

/**
 * Prognosewerte, die der Server bei jedem Laden ab „jetzt“ neu rechnet (Zeitplan in `nachher`/`bestehend.plan`):
 * Zeitpunkte sowie Schrittzahl und Endpreis des begrenzten Simulationshorizonts. Gesendet wird nur das Ziel.
 */
const ZEITPUNKTE = new Set(['faellig_ab', 'naechster_versuch', 'ziel_am', 'schritte', 'endpreis'])

/**
 * Vergleichsstand eines Starts ohne diese Zeitpunkte. Alle fachlichen Werte bleiben im Vergleich:
 * Ziel, Startbarkeit, Regel, Floor, Wirkung, Folge, Ist-Preis und nächster Preis der Prognose.
 */
export const startStand = (s: FamilyStart): string =>
  JSON.stringify(s, (k, wert: unknown) => (ZEITPUNKTE.has(k) ? undefined : wert))

/** null = auswählbar, sonst der Grund (gleiche Texte wie die Startzeile). */
export function sammelAuswaehlbar(v: FamilyVariante, killAktiv: boolean, sperre: string | null): string | null {
  const s = v.start.base
  if (killAktiv) return 'Not-Aus aktiv'
  if (sperre !== null) return sperre
  if (!s.startbar || s.ziel === null) return grundText(s) ?? 'Start nicht möglich'
  if (startAmZiel(v, 'base')) return 'Preis am Ziel'
  return null
}

export interface SammelEintrag { sku: string; bestaetigt: string }
export type SammelArt = 'ok' | 'warnung' | 'fehler' | 'unklar' | 'veraltet' | 'notaus' | 'angehalten' | 'abgebrochen'
export interface SammelErgebnis { sku: string; art: SammelArt; text: string }

const TEXT: Partial<Record<SammelArt, string>> = {
  veraltet: 'Daten geändert – nicht gestartet',
  notaus: 'Not-Aus aktiv – nicht gestartet',
  angehalten: 'angehalten – nicht gestartet',
  abgebrochen: 'Serie abgebrochen – nicht gestartet',
}

/** Ausgang des Starts unbekannt: Netzabbruch oder Antwort ohne lesbare Bestätigung. */
const ausgangUnklar = (e: unknown) => !(e instanceof ApiError) || e.undecodable

export async function sammelSerie({ eintraege, ladeFamilie, starte, angehalten, onErgebnis }: {
  eintraege: SammelEintrag[]
  ladeFamilie: () => Promise<RolloutFamily>
  starte: (sku: string, portal: string, ziel: number) => Promise<CreateRolloutAck>
  angehalten: () => boolean
  onErgebnis?: (e: SammelErgebnis, index: number) => void
}): Promise<SammelErgebnis[]> {
  const out: SammelErgebnis[] = []
  const melde = (e: SammelErgebnis) => { out.push(e); onErgebnis?.(e, out.length - 1) }
  const rest = (ab: number, art: SammelArt) => { for (const e of eintraege.slice(ab)) melde({ sku: e.sku, art, text: TEXT[art]! }) }

  for (const [i, e] of eintraege.entries()) {
    if (angehalten()) { rest(i, 'angehalten'); break }
    let fam: RolloutFamily
    try { fam = await ladeFamilie() } catch {
      melde({ sku: e.sku, art: 'fehler', text: 'Daten nicht neu geladen – Serie gestoppt' })
      rest(i + 1, 'abgebrochen'); break
    }
    if (fam.kill_aktiv) { rest(i, 'notaus'); break }
    const v = fam.varianten.find((x) => x.sku === e.sku)
    const s = v?.start.base
    if (!v || !s || startStand(s) !== e.bestaetigt || !s.startbar || s.ziel === null) {
      melde({ sku: e.sku, art: 'veraltet', text: TEXT.veraltet! })
      continue
    }
    try {
      const m = startMeldung(await starte(v.sku, s.portal, s.ziel))
      melde({ sku: e.sku, art: m.art, text: m.text })
    } catch (err) {
      if (ausgangUnklar(err)) {
        melde({ sku: e.sku, art: 'unklar', text: 'Ausgang unklar – bitte Rollout-Liste prüfen; Serie gestoppt' })
        rest(i + 1, 'abgebrochen'); break
      }
      melde({ sku: e.sku, art: 'fehler', text: fehlerText((err as ApiError).message) })
    }
  }
  return out
}

export function sammelZusammenfassung(r: SammelErgebnis[]): string {
  const gestartet = r.filter((e) => e.art === 'ok' || e.art === 'warnung').length
  const fehler = r.filter((e) => e.art === 'fehler' || e.art === 'unklar').length
  const nicht = r.length - gestartet - fehler
  return `${gestartet} gestartet, ${fehler} Fehler, ${nicht} nicht gestartet`
}
